import type { DetectionBox } from "@/components/phone-capture/capture-wizard/commons";
import logger from "@/lib/logging/logger";
import type { NativeCameraCapturePayload } from "@/lib/native-bridge";
import { nativeCamera } from "./nativeCamera";

export type StillFrame = {
	ctx: CanvasRenderingContext2D;
	width: number;
	height: number;
	/** The part of the frame the slot showed, in frame pixels; the overlay guides map onto it. */
	visibleBox: DetectionBox;
};

/** Where a full-resolution still comes from; the caller owns and releases the returned canvas. */
export type StillSource = { grab: () => Promise<StillFrame> };

const fullBox = (width: number, height: number): DetectionBox => ({ x: 0, y: 0, w: width, h: height });

const createFrameContext = (width: number, height: number): CanvasRenderingContext2D => {
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Could not get 2D context.");
	return ctx;
};

export function browserStillSource(video: HTMLVideoElement): StillSource {
	return {
		async grab() {
			const ready =
				video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0;
			if (!ready) {
				logger.warning("INFO", "Video not ready for capture", {
					readyState: video.readyState,
					videoWidth: video.videoWidth,
					videoHeight: video.videoHeight,
				});
				throw new Error("Video not ready for capture");
			}
			const { videoWidth: width, videoHeight: height } = video;
			const ctx = createFrameContext(width, height);
			ctx.drawImage(video, 0, 0, width, height);
			return { ctx, width, height, visibleBox: fullBox(width, height) };
		},
	};
}

async function decodeJpeg(jpegBase64: string): Promise<ImageBitmap> {
	const binary = atob(jpegBase64);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
	return createImageBitmap(new Blob([bytes], { type: "image/jpeg" }));
}

/** Still photos from the wrapper's camera, decoded into the same canvas shape the browser path yields. */
export function nativeStillSource(options: Omit<NativeCameraCapturePayload, "requestId"> = {}): StillSource {
	return {
		async grab() {
			const photo = await nativeCamera.capture(options);
			const bitmap = await decodeJpeg(photo.jpegBase64);
			try {
				const { width, height } = bitmap;
				const ctx = createFrameContext(width, height);
				ctx.drawImage(bitmap, 0, 0);
				const visible = nativeCamera.getSnapshot().geometry?.previewVisibleRect;
				const visibleBox =
					visible && visible.width > 0 && visible.height > 0
						? {
								x: visible.x * width,
								y: visible.y * height,
								w: visible.width * width,
								h: visible.height * height,
							}
						: fullBox(width, height);
				return { ctx, width, height, visibleBox };
			} finally {
				bitmap.close();
			}
		},
	};
}
